import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Shepherd from "shepherd.js";

import ButtonIcon from "../../images/rebrandIcons/ButtonIcon.png";

function createTourButtonHTML(label, direction = null, t) {
  const text = t(label);

  const arrowLeft = `<img class="h-2.5 mr-3 mb-1 transform rotate-180" src="${ButtonIcon}" alt="${text} Arrow Icon" />`;
  const arrowRight = `<img class="h-2.5 ml-3 mb-1" src="${ButtonIcon}" alt="${text} Arrow Icon" />`;

  return `
    <div class="flex gap-1 items-center">
      ${direction === "left" ? arrowLeft : ""}
      <p>${text}</p>
      ${direction === "right" ? arrowRight : ""}
    </div>
  `;
};

/**
 * Monitors DOM for dialog/modal openings and pauses the tour
 * @param {Shepherd.Tour} tour - The active Shepherd tour instance
 * @returns {Function} Cleanup function to remove observer
 */
function setupDialogMonitor(tour) {
  if (!tour)
    return () => {};

  // Track if we've paused the tour due to a dialog
  let isPausedByDialog = false;

  const checkForOpenDialogs = () => {
    // Check for Radix UI Dialog portals - they render inside body
    // Look for any dialog overlay with data-state="open"
    const dialogOverlays = document.querySelectorAll("[data-radix-popper-content-wrapper], [role=\"dialog\"]");

    // Also check for any element with backdrop-blur (your Dialog overlay has this)
    const backdropElements = document.querySelectorAll(".backdrop-blur-xl");

    const hasOpenDialog = dialogOverlays.length > 0 || backdropElements.length > 0;

    if (hasOpenDialog && !isPausedByDialog && tour.isActive()) {
      // Dialog opened - hide tour overlay temporarily
      isPausedByDialog = true;
      const shepherdModal = document.querySelector(".shepherd-modal-overlay-container");
      if (shepherdModal) {
        shepherdModal.style.display = "none";
      }
      const shepherdElement = document.querySelector(".shepherd-element");
      if (shepherdElement) {
        shepherdElement.style.display = "none";
      }
    }
    else if (!hasOpenDialog && isPausedByDialog) {
      // Dialog closed - restore tour overlay
      isPausedByDialog = false;
      const shepherdModal = document.querySelector(".shepherd-modal-overlay-container");
      if (shepherdModal) {
        shepherdModal.style.display = "";
      }
      const shepherdElement = document.querySelector(".shepherd-element");
      if (shepherdElement) {
        shepherdElement.style.display = "";
      }
    }
  };

  // MutationObserver to detect when dialogs are added/removed from DOM
  const observer = new MutationObserver(() => {
    checkForOpenDialogs();
  });

  // Observe the entire document for changes
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["data-state", "role", "class"], // Watch for state changes
  });

  // Initial check
  checkForOpenDialogs();

  return () => observer.disconnect();
}

function useSongsCreateUpdateTour() {
  const { t } = useTranslation(["tutorial", "onboarding", "common"]);
  const tourRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (tourRef.current) {
        tourRef.current.cancel();
      }
    };
  }, []);

  const startTour = useCallback(() => {
    const tour = new Shepherd.Tour({
      useModalOverlay: true,
      defaultStepOptions: {
        classes: "bg-white font-sentric rounded-[10px] text-gray-800 shadow-xl p-6",
        cancelIcon: { enabled: true },
        scrollTo: { behavior: "smooth", block: "center" },
      },
    });

    // Store tour reference for cleanup
    tourRef.current = tour;

    // Setup dialog monitoring when tour starts
    let cleanupDialogMonitor;
    tour.on("start", () => {
      cleanupDialogMonitor = setupDialogMonitor(tour);
    });

    // Cleanup dialog monitor when tour ends
    tour.on("complete", () => {
      if (cleanupDialogMonitor)
        cleanupDialogMonitor();
    });
    tour.on("cancel", () => {
      if (cleanupDialogMonitor)
        cleanupDialogMonitor();
    });

    // The Song Spotify Step
    tour.addStep({
      id: "spotifyComponentTour",
      text: t("tutorial:songs-spotify-import"),
      attachTo: {
        element: "#spotify-component",
        on: "bottom",
      },
      modalOverlayOpeningPadding: 10,
      modalOverlayOpeningRadius: 8,
      classes: "ml-2",
      buttons: [
        {
          text: createTourButtonHTML("onboarding:back-button", "left", t),
          action: tour.back,
        },
        {
          text: createTourButtonHTML("common:next-button", "right", t),
          action: tour.next,
        },
      ],
    });

    // The Song Title(s) Step
    tour.addStep({
      id: "songsTitlesAltsTour",
      text: t("tutorial:songs-alternative-title"),
      attachTo: {
        element: "#songsTitlesAlts",
        on: "bottom",
      },
      modalOverlayOpeningPadding: 10,
      modalOverlayOpeningRadius: 8,
      buttons: [
        {
          text: createTourButtonHTML("onboarding:back-button", "left", t),
          action: tour.back,
        },
        {
          text: createTourButtonHTML("common:next-button", "right", t),
          action: tour.next,
        },
      ],
    });

    // The Song Form Duration Step
    tour.addStep({
      id: "songFormDurationTour",
      text: t("tutorial:songs-duration"),
      attachTo: {
        element: "#songsDurationSection",
        on: "bottom",
      },
      modalOverlayOpeningPadding: 10,
      modalOverlayOpeningRadius: 8,
      buttons: [
        {
          text: createTourButtonHTML("onboarding:back-button", "left", t),
          action: tour.back,
        },
        {
          text: createTourButtonHTML("common:next-button", "right", t),
          action: tour.next,
        },
      ],
    });

    // The Song Form Performers Step
    tour.addStep({
      id: "songPerformersSectionTour",
      text: t("tutorial:songs-performing-artists"),
      attachTo: {
        element: "#songPerformersSection",
        on: "bottom",
      },
      modalOverlayOpeningPadding: 10,
      modalOverlayOpeningRadius: 8,
      buttons: [
        {
          text: createTourButtonHTML("onboarding:back-button", "left", t),
          action: tour.back,
        },
        {
          text: createTourButtonHTML("common:next-button", "right", t),
          action: tour.next,
        },
      ],
    });

    // The Song Writer Container Step
    tour.addStep({
      id: "songWritersContainerTour",
      text: t("tutorial:songs-writers-container"),
      attachTo: {
        element: "#songWritersContainer",
        on: "bottom",
      },
      modalOverlayOpeningPadding: 10,
      modalOverlayOpeningRadius: 8,
      buttons: [
        {
          text: createTourButtonHTML("onboarding:back-button", "left", t),
          action: tour.back,
        },
        {
          text: createTourButtonHTML("common:next-button", "right", t),
          action: tour.next,
        },
      ],
    });

    // The Song Writer Splits Header Step
    tour.addStep({
      id: "songWriterSplitsHeaderTour",
      text: t("tutorial:songs-writer-splits"),
      attachTo: {
        element: "#songWriterSplitsHeader",
        on: "bottom",
      },
      modalOverlayOpeningPadding: 10,
      modalOverlayOpeningRadius: 8,
      buttons: [
        {
          text: createTourButtonHTML("onboarding:back-button", "left", t),
          action: tour.back,
        },
        {
          text: createTourButtonHTML("common:next-button", "right", t),
          action: tour.next,
        },
      ],
    });

    // The Song Recordings Container Step
    tour.addStep({
      id: "songRecordingsContainerTour",
      text: t("tutorial:songs-recordings-section"),
      attachTo: {
        element: "#songRecordingsContainer",
        on: "bottom",
      },
      modalOverlayOpeningPadding: 10,
      modalOverlayOpeningRadius: 8,
      buttons: [
        {
          text: createTourButtonHTML("onboarding:back-button", "left", t),
          action: tour.back,
        },
        {
          text: createTourButtonHTML("common:next-button", "right", t),
          action: tour.next,
        },
      ],
    });

    // The Song Remix Samples Section Step
    tour.addStep({
      id: "songsRemixSampleSectionTour",
      text: t("tutorial:songs-remix-samples"),
      attachTo: {
        element: "#songsRemixSampleSection",
        on: "bottom",
      },
      modalOverlayOpeningPadding: 10,
      modalOverlayOpeningRadius: 8,
      buttons: [
        {
          text: createTourButtonHTML("onboarding:back-button", "left", t),
          action: tour.back,
        },
        {
          text: createTourButtonHTML("common:next-button", "right", t),
          action: tour.next,
        },
      ],
    });

    // The Song End Buttons Section Step
    tour.addStep({
      id: "songsButtonsSectionTour",
      text: t("tutorial:songs-submit-cancel"),
      attachTo: {
        element: "#songsButtonsSection",
        on: "bottom",
      },
      modalOverlayOpeningPadding: 10,
      modalOverlayOpeningRadius: 8,
      buttons: [
        {
          text: createTourButtonHTML("onboarding:back-button", "left", t),
          action: tour.back,
        },
        {
          text: createTourButtonHTML("onboarding:complete-button", null, t),
          action: () => {
            localStorage.setItem("songsCreateUpdateTourCompleted", "true");
            tour.complete();
          },
        },
      ],
    });

    tour.start();
  }, [t]);

  return startTour;
}

export default useSongsCreateUpdateTour;
